import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AlertTriangle, CalendarDays, CheckCircle2, Link2, Loader2, Settings2, Unlink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Button, IconButton } from '../../components/UI';

interface GoogleStatus {
  configured: boolean;
  clientId: string;
  clientSecret: string;
  status: 'disconnected' | 'connected' | 'error';
  connected: boolean;
  accountEmail: string;
  calendarId: string;
  lastError: string;
  redirectUri: string;
}

const inputClass = 'field';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

// Integração com o Google Agenda (OAuth de uma conta Gmail): sala do Meet por reserva + bloqueio de ocupados
const GoogleCalendarPanel: React.FC = () => {
  const { t } = useTranslation();
  const { isSuperAdmin } = useAuth();
  const notifications = useNotifications();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState<GoogleStatus | null>(null);
  const [showSetup, setShowSetup] = useState(false);
  const [form, setForm] = useState({ clientId: '', clientSecret: '', calendarId: 'primary' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get('/google-calendar/status');
      setStatus(data);
      setForm({ clientId: data.clientId || '', clientSecret: data.clientSecret || '', calendarId: data.calendarId || 'primary' });
    } catch (error) {
      console.error('Erro ao carregar status do Google Agenda:', error);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Retorno do consentimento do Google (/admin/agendamentos?google=connected|error&googleMsg=...)
  useEffect(() => {
    const result = searchParams.get('google');
    if (!result) return;
    if (result === 'connected') notifications.showSuccess(t('agendamentoAdmin.gConnectedOk'), '');
    else notifications.showError(t('agendamentoAdmin.gConnectError'), searchParams.get('googleMsg') || '');
    const next = new URLSearchParams(searchParams);
    next.delete('google');
    next.delete('googleMsg');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notifyError = (error: any) =>
    notifications.showError(t('agendamentoAdmin.error'), error.response?.data?.message || t('agendamentoAdmin.tryAgain'));

  const saveCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.put('/google-calendar/credentials', form);
      setStatus(data);
      setForm({ clientId: data.clientId, clientSecret: data.clientSecret, calendarId: data.calendarId });
      notifications.showSuccess(t('agendamentoAdmin.gCredentialsSaved'), '');
    } catch (error) {
      notifyError(error);
    } finally {
      setBusy(false);
    }
  };

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get('/google-calendar/auth-url');
      window.location.href = data.url;
    } catch (error) {
      notifyError(error);
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!(await confirm({ message: t('agendamentoAdmin.gDisconnectConfirm'), confirmText: t('agendamentoAdmin.gDisconnect'), variant: 'warning' }))) return;
    setBusy(true);
    try {
      const { data } = await axios.post('/google-calendar/disconnect');
      setStatus(data);
    } catch (error) {
      notifyError(error);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  const state = status.connected ? 'connected' : status.status === 'error' ? 'error' : 'disconnected';
  const tone = {
    connected: { icon: <CheckCircle2 className="w-5 h-5 text-green-600" />, title: t('agendamentoAdmin.gConnectedAs', { email: status.accountEmail }), desc: t('agendamentoAdmin.gConnectedDesc') },
    error: { icon: <AlertTriangle className="w-5 h-5 text-amber-600" />, title: t('agendamentoAdmin.gError'), desc: status.lastError || t('agendamentoAdmin.gErrorDesc') },
    disconnected: { icon: <CalendarDays className="w-5 h-5 text-gray-400" />, title: t('agendamentoAdmin.gDisconnected'), desc: t('agendamentoAdmin.gDisconnectedDesc') }
  }[state];

  return (
    <div className="bg-white rounded-lg shadow mb-6 p-4 sm:p-6">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="mt-0.5 shrink-0">{tone.icon}</div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('agendamentoAdmin.gTitle')}</p>
            <p className="font-medium text-gray-900 break-all">{tone.title}</p>
            <p className="text-sm text-gray-600 mt-0.5">{tone.desc}</p>
          </div>
        </div>
        {isSuperAdmin ? (
          <div className="flex flex-wrap gap-2 shrink-0">
            {status.configured && state !== 'connected' && (
              <Button type="button" onClick={connect} disabled={busy}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                {state === 'error' ? t('agendamentoAdmin.gReconnect') : t('agendamentoAdmin.gConnect')}
              </Button>
            )}
            {state === 'connected' && (
              <Button variant="ghost" size="sm" className="text-red-700 hover:bg-red-50 border-red-200"
                type="button"
                onClick={disconnect}
                disabled={busy}>
                <Unlink className="w-4 h-4" />
                {t('agendamentoAdmin.gDisconnect')}
              </Button>
            )}
            <Button variant="secondary" type="button" onClick={() => setShowSetup((v) => !v)}>
              <Settings2 className="w-4 h-4" />
              {showSetup ? t('agendamentoAdmin.gHideSetup') : t('agendamentoAdmin.gSetup')}
            </Button>
          </div>
        ) : (
          state !== 'connected' && <p className="text-xs text-gray-500 md:max-w-[14rem]">{t('agendamentoAdmin.gOnlySuperAdmin')}</p>
        )}
      </div>

      {isSuperAdmin && showSetup && (
        <div className="mt-5 pt-5 border-t border-gray-100 grid gap-6 lg:grid-cols-2">
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-2">{t('agendamentoAdmin.gSteps')}</p>
            <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-700">
              <li>{t('agendamentoAdmin.gStep1')}</li>
              <li>{t('agendamentoAdmin.gStep2')}</li>
              <li>{t('agendamentoAdmin.gStep3')}</li>
              <li>{t('agendamentoAdmin.gStep4')}</li>
            </ol>
          </div>
          <form onSubmit={saveCredentials} className="space-y-4">
            <div>
              <label className={labelClass}>{t('agendamentoAdmin.gRedirectUri')}</label>
              <div className="flex gap-2">
                <input readOnly value={status.redirectUri} className={`${inputClass} bg-gray-50 font-mono text-xs`} onFocus={(e) => e.target.select()} />
                <IconButton action="copyLink" value={status.redirectUri} className="shrink-0" />
              </div>
            </div>
            <div>
              <label className={labelClass}>{t('agendamentoAdmin.gClientId')}</label>
              <input
                type="text"
                value={form.clientId}
                onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                placeholder="xxxx.apps.googleusercontent.com"
                className={inputClass}
                autoComplete="off"
                required
              />
            </div>
            <div>
              <label className={labelClass}>{t('agendamentoAdmin.gClientSecret')}</label>
              <input
                type="password"
                value={form.clientSecret}
                onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
                className={inputClass}
                autoComplete="new-password"
                required
              />
            </div>
            <div>
              <label className={labelClass}>{t('agendamentoAdmin.gCalendarId')}</label>
              <input
                type="text"
                value={form.calendarId}
                onChange={(e) => setForm({ ...form, calendarId: e.target.value })}
                className={inputClass}
              />
              <p className="text-xs text-gray-500 mt-1">{t('agendamentoAdmin.gCalendarIdHint')}</p>
            </div>
            <Button type="submit" disabled={busy}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('agendamentoAdmin.gSaveCredentials')}
            </Button>
          </form>
        </div>
      )}
    </div>
  );
};

export default GoogleCalendarPanel;
